import {
  getGetGrafoNodoQueryKey,
  getGetNodoPortaleQueryKey,
  getGetNodoQueryKey,
  NodoCreateOrUpdateDto,
  NodoDto,
  NodoTipo,
  PortaleId,
  useCreateNodo,
  useGetModelliDispositivo,
  useGetModelliGateway,
  useGetNodo,
  useGetNodoIcone,
  useGetPortali,
  UserInternoDto,
  useUpdateNodo,
} from "@/api";
import Button from "@/elements/Button";
import { portaleConfig } from "@/portaliConfig";
import { NodoTipoLabel } from "@/utils/nodoUtils";
import { withOpeningReset } from "@/utils/withOpeningReset";
import {
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import { useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useCallback, useEffect, useMemo } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import NodoImmaginiCard, {
  ImmagineEsistenteRef,
  JustUploadedRef,
} from "../CentraleDetailsPage/NodoCard";
import Loading, { SmallLoading } from "../Loading";
import UserPicker from "../UserPicker";
import NodoDialogImmagineDinamica from "./NodoDialogImmagineDinamica";
import NodoDialogSectionElaboranet from "./NodoDialogSectionElaboranet";

export type NodoDialogProps = {
  open?: boolean;
  onClose?: (NodoDto: NodoDto | null) => void;
  nodoId?: number | null;
  padreId?: number | null;
  tipoNodo?: NodoTipo;
};

function NodoDialog({
  open = false,
  onClose,
  nodoId,
  padreId,
  tipoNodo,
}: NodoDialogProps) {
  const { data: nodo, isLoading: isLoadingNodo } = useGetNodo(nodoId ?? 0, {
    query: {
      enabled: !!nodoId,
    },
  });

  const { data: padre, isLoading: IsLoadingPadre } = useGetNodo(padreId ?? 0, {
    query: {
      enabled: !!padreId,
    },
  });

  const handleClose = useCallback(() => {
    onClose?.(null);
  }, [onClose]);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      {(nodoId && isLoadingNodo) || (padreId && IsLoadingPadre) ? (
        <Loading />
      ) : (
        <NodoDialogWrapper
          nodo={nodo?.data ?? null}
          padre={padre?.data ?? null}
          onClose={onClose}
          tipoNodo={tipoNodo}
        />
      )}
    </Dialog>
  );
}

function NodoDialogWrapper({
  onClose,
  nodo,
  padre,
  tipoNodo: _tipoNodo,
}: {
  onClose?: (NodoDto: NodoDto | null) => void;
  nodo?: NodoDto | null;
  padre?: NodoDto | null;
  tipoNodo?: NodoTipo;
}) {
  const { t } = useTranslation();
  const isNew = !nodo;
  const _tipoNodoInitial = _tipoNodo || nodo?.tipo || "Contenitore";
  const getTipoLabel = (tipo: NodoTipo) => NodoTipoLabel[tipo].label(t);
  const getTipoNuovoLabel = (tipo: NodoTipo) =>
    NodoTipoLabel[tipo].labelWithNewPrefix(t);
  const getTipoCreatoLabel = (tipo: NodoTipo) =>
    NodoTipoLabel[tipo].labelCreated(t);

  const {
    handleSubmit,
    formState: { isValid, errors },
    control,
    register,
    setValue,
    setError,
  } = useForm<NodoCreateOrUpdateDto>({
    defaultValues: {
      nome: nodo?.nome || "",
      contenitoreIconaId: nodo?.contenitoreIconaId ?? undefined,
      portaliIds: nodo?.portali?.map((p) => p.portaleId) || [],
      tipo: _tipoNodoInitial,
      padreId: padre?.id ?? undefined,
      referenteCommercialeId: nodo?.referenteCommercialeId ?? undefined,
      haAlloggiamentiFiltro: nodo?.haAlloggiamentiFiltro ?? false,
      haSerbatoioOlio: nodo?.haSerbatoioOlio ?? false,
      haStoricoBonifiche: nodo?.haStoricoBonifiche ?? false,
      haStoricoFiltrazioni: nodo?.haStoricoFiltrazioni ?? false,
      haStoricoManutenzioni: nodo?.haStoricoManutenzioni ?? false,
      haAnalisi: nodo?.haAnalisi ?? false,
      analisiInviaAlCommerciale: nodo?.analisiInviaAlCommerciale ?? true,
      analisiInviaAlAreaManager: nodo?.analisiInviaAlAreaManager ?? false,
      analisiDurataDefaultMesi:
        nodo?.analisiDurataDefaultMesi ??
        (_tipoNodoInitial === "Azienda" ? 6 : null),
      modelloDispositivoId: nodo?.dispositivoModello?.id || undefined,
      gatewayModelloId: nodo?.gatewayModello?.id || undefined,
      matricola: nodo?.dispositivoMatricola || null,
      elaboranetTargetId: nodo?.elaboranetTargetId,
      immagini:
        nodo?.immagini?.map((x) => ({ immagineEsistenteId: x.id })) ?? [],
      haImmagineDinamica: nodo?.haImmagineDinamica ?? false,
      immagineDinamicaSvgId: nodo?.immagineDinamicaSvgId ?? null,
      immagineDinamicaElementi:
        nodo?.immagineDinamicaElementi?.map((elemento) => ({
          svgElementId: elemento.svgElementId,
          nodoDispositivoId: elemento.nodoDispositivoId,
        })) ?? [],
    },
  });

  const { mutateAsync: createNodo, isLoading: createLoading } = useCreateNodo();
  const { mutateAsync: updateNodo, isLoading: updateLoading } = useUpdateNodo();

  // Monitora il tipo selezionato
  const tipoNodo = useWatch({
    control: control,
    name: "tipo",
  });

  const availablePortali = useGetPortali();
  const portaliPadre = useGetPortaliNodoPadre(tipoNodo, nodo, padre);
  const haImmagineDinamica = useWatch({
    control,
    name: "haImmagineDinamica",
  });
  const immagineDinamicaSvgId = useWatch({
    control,
    name: "immagineDinamicaSvgId",
  });

  useEffect(() => {
    if (portaliPadre && isNew) {
      setValue("portaliIds", portaliPadre);
    }
  }, [portaliPadre, isNew]);

  const modelli = useGetModelliDispositivo();
  const modelliGateway = useGetModelliGateway({
    query: {
      enabled: tipoNodo === "Gateway",
    },
  });
  const icone = useGetNodoIcone();

  // Filtra i modelli in base al tipo dispositivo
  const modelliDisponibili = useMemo(() => {
    if (
      !modelli.data?.data ||
      (tipoNodo !== "Dispositivo_Macchinario" &&
        tipoNodo !== "Dispositivo_Sensore")
    ) {
      return [];
    }

    const tipoDispositivo =
      tipoNodo === "Dispositivo_Macchinario" ? "macchinario" : "sensore";
    return modelli.data.data.filter(
      (modello) => modello.tipoDispositivo === tipoDispositivo
    );
  }, [modelli.data?.data, tipoNodo]);

  const modelliGatewayDisponibili = useMemo(
    () => modelliGateway.data?.data ?? [],
    [modelliGateway.data?.data]
  );

  useEffect(() => {
    if (!haImmagineDinamica) {
      setValue("immagineDinamicaSvgId", null, { shouldDirty: true });
      setValue("immagineDinamicaElementi", [], { shouldDirty: true });
    }
  }, [haImmagineDinamica, setValue]);

  // Determina se mostrare i campi dispositivo
  const isDispositivo =
    tipoNodo === "Dispositivo_Macchinario" ||
    tipoNodo === "Dispositivo_Sensore";

  const isGateway = tipoNodo === "Gateway";

  const isAzienda = tipoNodo === "Azienda";

  const applyServerErrors = useCallback(
    (error: unknown) => {
      if (!(error instanceof AxiosError)) {
        return false;
      }

      const errors = (
        error.response?.data as { errors?: Record<string, string[] | string> }
      )?.errors;

      if (!errors) {
        return false;
      }

      Object.entries(errors).forEach(([key, value]) => {
        const message = Array.isArray(value) ? value.join("\n") : value;
        if (message) {
          setError(key as any, { message });
        }
      });

      return true;
    },
    [setError]
  );

  const queryClient = useQueryClient();

  const onSubmit = async (data: NodoCreateOrUpdateDto) => {
    if (isNew) {
      try {
        const n = await createNodo({
          data: data,
        });

        toast.success(
          t("nodoDialog.toast.creato", {
            tipo: getTipoCreatoLabel(data.tipo),
          })
        );

        onClose?.(n.data);
      } catch (e: any) {
        applyServerErrors(e);
        const message =
          e instanceof AxiosError && typeof e.response?.data === "string"
            ? e.response?.data
            : t("nodoDialog.errori.creazione");
        toast.error(message);
      }
    } else {
      try {
        await updateNodo({
          id: nodo.id,
          data: data,
        });

        await queryClient.invalidateQueries(getGetNodoQueryKey(nodo.id));
        await queryClient.invalidateQueries(
          getGetNodoPortaleQueryKey(portaleConfig.id, nodo.id)
        );

        toast.success(t("nodoDialog.toast.salvato"));
        onClose?.(null);
      } catch (e) {
        console.error("updateNodo", e);
        applyServerErrors(e);
        const message =
          e instanceof AxiosError && typeof e.response?.data === "string"
            ? e.response?.data
            : t("nodoDialog.errori.salvataggio");
        toast.error(message);
      }
    }

    // Invalidate grafo if padre exists (aziendaPadreId logic is now in child component)
    if (padre?.id) {
      await queryClient.invalidateQueries(getGetGrafoNodoQueryKey(padre.id));
      await queryClient.invalidateQueries(
        getGetNodoPortaleQueryKey(portaleConfig.id, padre.id)
      );
    }
  };

  const isLoading = createLoading || updateLoading;

  const haAnalisi = useWatch({
    control: control,
    name: "haAnalisi",
  });

  const durataAnalisiDaGerarchia = useMemo(() => {
    const durataDefault = 6;
    const startingNode = nodo ?? padre;

    if (startingNode?.analisiDurataDefaultMesi) {
      return startingNode?.analisiDurataDefaultMesi;
    } else if (!startingNode?.breadcrumbs) {
      return durataDefault;
    } else {
      for (let i = startingNode.breadcrumbs.length - 1; i >= 0; i--) {
        if (startingNode.breadcrumbs[i].nodo.analisiDurataDefaultMesi) {
          return startingNode.breadcrumbs[i].nodo.analisiDurataDefaultMesi;
        }
      }

      return durataDefault;
    }
  }, [nodo, padre]);

  return (
    <>
      <DialogTitle>
        {nodo
          ? t("nodoDialog.titoloModifica", {
              tipo: getTipoLabel(nodo.tipo),
              nome: nodo.nome,
            })
          : tipoNodo
          ? t("nodoDialog.titoloCreaTipo", {
              tipo: getTipoNuovoLabel(tipoNodo),
            })
          : t("nodoDialog.titoloCreaPosizione")}
      </DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={3} pt={1}>
          {/* Modello Dispositivo - Solo per macchinari e sensori */}
          {isDispositivo && (
            <Box>
              <InputLabel
                htmlFor="nodo-modello"
                sx={{ fontSize: "1.1rem", fontWeight: "800 !important" }}
              >
                {t("nodoDialog.campi.modelloDispositivo")}
              </InputLabel>
              <Controller
                name="modelloDispositivoId"
                control={control}
                rules={{
                  required: isDispositivo
                    ? t("nodoDialog.errori.modelloDispositivoRichiesto")
                    : false,
                }}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <Autocomplete
                      onChange={(e, v) => {
                        field.onChange(v?.id ?? undefined);
                      }}
                      value={
                        modelliDisponibili.find((m) => m.id === field.value) ??
                        null
                      }
                      size="small"
                      options={modelliDisponibili}
                      getOptionLabel={(m) => m.modello}
                      renderInput={(props) => (
                        <TextField
                          {...props}
                          id="nodo-modello"
                          error={!!error}
                          fullWidth
                        />
                      )}
                      disabled={modelliDisponibili.length === 0 || !isNew}
                    />
                    {error && (
                      <FormHelperText error>{error.message}</FormHelperText>
                    )}
                    {modelliDisponibili.length === 0 && (
                      <FormHelperText>
                        {t("nodoDialog.messaggi.modelliNonDisponibili")}
                      </FormHelperText>
                    )}
                  </>
                )}
              />
            </Box>
          )}

          {isGateway && (
            <Box>
              <InputLabel
                htmlFor="nodo-modello-gateway"
                sx={{ fontSize: "1.1rem", fontWeight: "800 !important" }}
              >
                {t("nodoDialog.campi.modelloGateway")}
              </InputLabel>
              <Controller
                name="gatewayModelloId"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <Autocomplete
                      onChange={(e, v) => {
                        field.onChange(v?.id ?? undefined);
                      }}
                      value={
                        modelliGatewayDisponibili.find(
                          (m) => m.id === field.value
                        ) ?? null
                      }
                      size="small"
                      options={modelliGatewayDisponibili}
                      getOptionLabel={(m) => `${m.marca} ${m.nome}`}
                      renderInput={(props) => (
                        <TextField
                          {...props}
                          id="nodo-modello-gateway"
                          error={!!error}
                          fullWidth
                        />
                      )}
                      disabled={modelliGatewayDisponibili.length === 0}
                    />
                    {error && (
                      <FormHelperText error>{error.message}</FormHelperText>
                    )}
                    {modelliGatewayDisponibili.length === 0 && (
                      <FormHelperText>
                        {t("nodoDialog.messaggi.modelliGatewayNonDisponibili")}
                      </FormHelperText>
                    )}
                  </>
                )}
              />
            </Box>
          )}

          {/* Matricola dispositivo - Solo per macchinari e sensori */}
          {isDispositivo && (
            <Box>
              <InputLabel
                htmlFor="nodo-matricola"
                sx={{ fontSize: "1.1rem", fontWeight: "800 !important" }}
              >
                {t("nodoDialog.campi.matricola")}
              </InputLabel>
              <TextField
                id="nodo-matricola"
                {...register("matricola", {
                  required: isDispositivo
                    ? t("nodoDialog.errori.matricolaRichiesta")
                    : false,
                })}
                error={!!errors.matricola}
                helperText={errors.matricola?.message}
                fullWidth
                disabled={!isNew}
              />
            </Box>
          )}

          {/* Nome */}
          {!isDispositivo && !isAzienda && (
            <Box>
              <InputLabel
                htmlFor="nodo-nome"
                sx={{ fontSize: "1.1rem", fontWeight: "800 !important" }}
              >
                {t("nodoDialog.campi.nome")}
              </InputLabel>
              <TextField
                id="nodo-nome"
                {...register("nome", {
                  required: t("nodoDialog.errori.nomeRichiesto"),
                })}
                error={!!errors.nome}
                helperText={errors.nome?.message}
                fullWidth
              />
            </Box>
          )}

          {/* Icona */}
          {tipoNodo === "Contenitore" && (
            <Box>
              <InputLabel
                htmlFor="nodo-icona"
                sx={{ fontSize: "1.1rem", fontWeight: "800 !important" }}
              >
                {t("nodoDialog.campi.icona")}
              </InputLabel>
              <Controller
                name="contenitoreIconaId"
                control={control}
                rules={{ required: t("nodoDialog.errori.iconaRichiesta") }}
                render={({ field }) => (
                  <Select
                    {...field}
                    id="nodo-icona"
                    error={!!errors.contenitoreIconaId}
                    fullWidth
                    displayEmpty
                    renderValue={(value) => {
                      if (!value) {
                        return t("nodoDialog.campi.iconaPlaceholder");
                      }
                      const selectedIcon = icone.data?.data?.find(
                        (icon) => icon.id === value
                      );
                      return (
                        <Box display="flex" alignItems="center" gap={1}>
                          {selectedIcon?.id && (
                            <img
                              src={`/api/nodo_icone/${selectedIcon.id}/img`}
                              alt={t("nodoDialog.alt.icona")}
                              style={{
                                width: 32,
                                height: 32,
                                objectFit: "contain",
                              }}
                            />
                          )}
                          <span>{selectedIcon?.nome}</span>
                        </Box>
                      );
                    }}
                  >
                    {icone.data?.data?.map((icona) => (
                      <MenuItem key={icona.id} value={icona.id}>
                        <Box display="flex" alignItems="center" gap={1}>
                          {icona.file?.fileName && (
                            <img
                              src={`/api/nodo_icone/${icona.id}/img`}
                              alt={t("nodoDialog.alt.icona")}
                              style={{
                                width: 32,
                                height: 32,
                                objectFit: "contain",
                              }}
                            />
                          )}
                          <span>
                            {icona.nome ??
                              t("nodoDialog.campi.iconaDefault", {
                                id: icona.id,
                              })}
                          </span>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              {errors.contenitoreIconaId && (
                <FormHelperText error>
                  {errors.contenitoreIconaId.message}
                </FormHelperText>
              )}
            </Box>
          )}

          {tipoNodo === "Contenitore" && (
            <Box>
              <InputLabel
                htmlFor="nodo-icona"
                sx={{ fontSize: "1.1rem", fontWeight: "800 !important" }}
              >
                {t("nodoDialog.campi.immagini")}
              </InputLabel>
              <Controller
                control={control}
                name="immagini"
                render={({ field }) => {
                  return (
                    <NodoImmaginiCard
                      editable
                      nodo={nodo ?? undefined}
                      immagini={
                        field.value?.map((x) => {
                          if (x.immagineEsistenteId) {
                            return {
                              id: x.immagineEsistenteId,
                            } as ImmagineEsistenteRef;
                          } else {
                            return {
                              justUploaded: true,
                              fileId: x.nuovaImmagineFileId!,
                            } as JustUploadedRef;
                          }
                        }) ?? []
                      }
                      onChange={(x) => {
                        field.onChange(
                          x?.map((y) => {
                            if ("justUploaded" in y) {
                              return {
                                nuovaImmagineFileId: y.fileId,
                              };
                            } else {
                              return {
                                immagineEsistenteId: y.id,
                              };
                            }
                          }) ?? []
                        );
                      }}
                    />
                  );
                }}
              />
            </Box>
          )}

          {/* Portali dinamici */}
          <Box>
            <InputLabel
              sx={{ fontSize: "1.1rem", fontWeight: "800 !important" }}
            >
              {t("nodoDialog.campi.portali")}
            </InputLabel>
            <Controller
              name="portaliIds"
              control={control}
              rules={{
                validate: (value) =>
                  value && value.length > 0
                    ? true
                    : t("nodoDialog.errori.portaleRichiesto"),
              }}
              render={({ field, fieldState: { error } }) => {
                return (
                  <Box sx={{ pl: 1 }}>
                    {availablePortali.data?.data.map((portale) => {
                      const disablePortale =
                        tipoNodo === "Contenitore" &&
                        portaliPadre &&
                        !portaliPadre.includes(portale.id);

                      const input = (
                        <FormControlLabel
                          key={portale.id}
                          disabled={!!disablePortale}
                          control={
                            <Checkbox
                              checked={
                                field.value?.includes(portale.id) || false
                              }
                              onChange={(e) => {
                                const currentIds = field.value || [];
                                if (e.target.checked) {
                                  field.onChange([...currentIds, portale.id]);
                                } else {
                                  field.onChange(
                                    currentIds.filter((id) => id !== portale.id)
                                  );
                                }
                              }}
                            />
                          }
                          label={portale.nome}
                        />
                      );

                      return disablePortale ? (
                        <Tooltip
                          title={t("nodoDialog.messaggi.portaleDisabilitato", {
                            portale: portale.nome,
                          })}
                        >
                          {input}
                        </Tooltip>
                      ) : (
                        input
                      );
                    })}
                    {error && (
                      <FormHelperText error>{error.message}</FormHelperText>
                    )}
                  </Box>
                );
              }}
            />
          </Box>

          {/* Referente Commerciale - Solo per Azienda e Contenitore */}
          {(tipoNodo === "Azienda" || tipoNodo === "Contenitore") && (
            <Box>
              <InputLabel
                htmlFor="nodo-referente-commerciale"
                sx={{ fontSize: "1.1rem", fontWeight: "800 !important" }}
              >
                {t("nodoDialog.campi.referenteCommerciale")}
              </InputLabel>
              <Controller
                name="referenteCommercialeId"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <UserPicker
                      role="Commerciale"
                      value={
                        nodo?.referenteCommerciale as
                          | UserInternoDto
                          | null
                          | undefined
                      }
                      onChange={(_, user) => {
                        field.onChange(user?.id ?? undefined);
                      }}
                      placeholder={t(
                        "nodoDialog.campi.referenteCommercialePlaceholder"
                      )}
                    />
                    {error && (
                      <FormHelperText error>{error.message}</FormHelperText>
                    )}
                  </>
                )}
              />
            </Box>
          )}

          {/* Proprietà del nodo */}
          {tipoNodo === "Contenitore" && (
            <Box sx={{ mb: 5 }}>
              <InputLabel
                sx={{ fontSize: "1.1rem", fontWeight: "800 !important" }}
              >
                {t("nodoDialog.proprieta.titolo")}
              </InputLabel>
              <Box sx={{ pl: 1 }}>
                <Controller
                  name="haSerbatoioOlio"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox {...field} checked={field.value} />}
                      label={t("nodoDialog.proprieta.serbatoioOlio")}
                    />
                  )}
                />
                <Controller
                  name="haAlloggiamentiFiltro"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox {...field} checked={field.value} />}
                      label={t("nodoDialog.proprieta.alloggiamentiFiltro")}
                    />
                  )}
                />
                <Controller
                  name="haStoricoBonifiche"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox {...field} checked={field.value} />}
                      label={t("nodoDialog.proprieta.storicoBonifiche")}
                    />
                  )}
                />
                <Controller
                  name="haStoricoFiltrazioni"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox {...field} checked={field.value} />}
                      label={t("nodoDialog.proprieta.storicoFiltrazioni")}
                    />
                  )}
                />
                <Controller
                  name="haStoricoManutenzioni"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox {...field} checked={field.value} />}
                      label={t("nodoDialog.proprieta.storicoManutenzioni")}
                    />
                  )}
                />
                <Box>
                  <Controller
                    name="haAnalisi"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Checkbox {...field} checked={field.value} />}
                        label={t("nodoDialog.proprieta.analisi")}
                      />
                    )}
                  />
                  <Box
                    sx={{
                      pl: 3,
                      ml: 1.5,
                      borderLeft: "2px solid",
                      borderColor: haAnalisi ? "primary.main" : "divider",
                      mb: 2,
                      mt: 1,
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                      transition: "all 0.2s",
                    }}
                  >
                    <Box
                      sx={{
                        opacity: haAnalisi ? 1 : 0.5,
                        pointerEvents: haAnalisi ? "auto" : "none",
                      }}
                    >
                      <NodoDialogSectionElaboranet
                        nodo={nodo}
                        padre={padre}
                        control={control}
                      />
                    </Box>

                    <Box>
                      <InputLabel>
                        {t("nodoDialog.campi.durataAnalisi")}
                      </InputLabel>
                      <Controller
                        name="analisiDurataDefaultMesi"
                        control={control}
                        rules={{
                          validate: (value) =>
                            value === null ||
                            (typeof value === "number" && value > 0) ||
                            t("nodoDialog.errori.durataAnalisi"),
                        }}
                        render={({ field, fieldState: { error } }) => {
                          const value = field.value;
                          return (
                            <Box display="flex" flexDirection="column" gap={1}>
                              <RadioGroup
                                value={value === null ? "parent" : "custom"}
                                onChange={(e) => {
                                  if (e.target.value === "parent") {
                                    field.onChange(null);
                                  } else {
                                    field.onChange(value === null ? 6 : value);
                                  }
                                }}
                                sx={{ pl: 1 }}
                              >
                                <FormControlLabel
                                  value="parent"
                                  control={<Radio size="small" />}
                                  label={t("nodoDialog.campi.durataNodoPadre", {
                                    mesi: durataAnalisiDaGerarchia,
                                  })}
                                />
                                <FormControlLabel
                                  value="custom"
                                  control={<Radio size="small" />}
                                  label={
                                    <Box
                                      display="flex"
                                      alignItems="center"
                                      gap={1}
                                    >
                                      <TextField
                                        type="number"
                                        inputProps={{ min: 1 }}
                                        value={value}
                                        onChange={(e) =>
                                          field.onChange(Number(e.target.value))
                                        }
                                        disabled={value === null}
                                        sx={{ width: 100 }}
                                        InputProps={{
                                          endAdornment: (
                                            <Typography
                                              variant="caption"
                                              mr={1}
                                            >
                                              {t("nodoDialog.campi.mesi")}
                                            </Typography>
                                          ),
                                        }}
                                        size="small"
                                      />
                                    </Box>
                                  }
                                />
                              </RadioGroup>
                              {error && (
                                <FormHelperText error>
                                  {error.message}
                                </FormHelperText>
                              )}
                            </Box>
                          );
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
                <Box>
                  <Controller
                    control={control}
                    name="haImmagineDinamica"
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={field.value ?? false}
                            onChange={(event) =>
                              field.onChange(event.target.checked)
                            }
                          />
                        }
                        label={t("nodoDialog.campi.immagineDinamicaAbilita")}
                      />
                    )}
                  />
                  <Box
                    sx={{
                      pl: 3,
                      ml: 1.5,
                      borderLeft: "2px solid",
                      borderColor: haImmagineDinamica
                        ? "primary.main"
                        : "divider",
                      mb: 1,
                      mt: 1,
                      transition: "all 0.2s",
                      opacity: haImmagineDinamica ? 1 : 0.5,
                      pointerEvents: haImmagineDinamica ? "auto" : "none",
                    }}
                  >
                    <NodoDialogImmagineDinamica control={control} />
                  </Box>
                </Box>
              </Box>
            </Box>
          )}

          {tipoNodo === "Azienda" && (
            <Box>
              <InputLabel>{t("nodoDialog.campi.durataAnalisi")}</InputLabel>
              <Controller
                name="analisiDurataDefaultMesi"
                control={control}
                rules={{
                  min: {
                    value: 1,
                    message: t("nodoDialog.errori.durataAnalisi"),
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <TextField
                      type="number"
                      inputProps={{ min: 1 }}
                      value={field.value}
                      onChange={field.onChange}
                      error={!!error}
                      sx={{ width: 100 }}
                      InputProps={{
                        endAdornment: (
                          <Typography variant="caption" mr={1}>
                            {t("nodoDialog.campi.mesi")}
                          </Typography>
                        ),
                      }}
                      size="small"
                    />
                    {error && (
                      <FormHelperText error>{error.message}</FormHelperText>
                    )}
                  </>
                )}
              />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        {isLoading && <SmallLoading sx={{ mr: 1 }} />}
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={isLoading}
          onClick={handleSubmit(onSubmit)}
        >
          {nodo ? t("azioni.salva") : t("nodoDialog.azioni.crea")}
        </Button>
        <Button
          variant="contained"
          color="light"
          disabled={isLoading}
          onClick={() => {
            onClose?.(null);
          }}
        >
          {t("azioni.annulla")}
        </Button>
      </DialogActions>
    </>
  );
}

export default withOpeningReset(NodoDialog);

function useGetPortaliNodoPadre(
  type: NodoTipo,
  nodo: NodoDto | null | undefined,
  _padre: NodoDto | null | undefined
): PortaleId[] | null {
  const shouldFetchPortaliNodoPadre = type === "Contenitore";
  const shouldFetchPadreFromNodo = !_padre;

  const idPadreFromNodo = useMemo(() => {
    if (!shouldFetchPadreFromNodo || !nodo) {
      return 0;
    }

    return nodo.breadcrumbs && nodo.breadcrumbs.length > 0
      ? nodo.breadcrumbs[nodo.breadcrumbs.length - 1].nodo.id
      : null;
  }, [shouldFetchPadreFromNodo]);
  const { data: padreFromNodo } = useGetNodo(idPadreFromNodo ?? 0, {
    query: {
      enabled: shouldFetchPortaliNodoPadre && shouldFetchPadreFromNodo,
    },
  });

  const padre = shouldFetchPadreFromNodo ? padreFromNodo?.data : _padre;

  if (!shouldFetchPortaliNodoPadre || !padre) {
    return null;
  }

  return padre.portali.map((p) => p.portaleId);
}
